/** Contains named sets of dimensions that are used for aggregation of metrics */
export enum DimensionSet {
  ByOrg = 'ByOrg',
  ByOrgAllSms = 'ByOrgAllSms',
  ByOrgAllWhatsApp = 'ByOrgAllWhatsApp',
  ByOrgBillingParty = 'ByOrgBillingParty',
  ByOrgBillingPartyRecipientCountry = 'ByOrgBillingPartyRecipientCountry',
  ByOrgProject = 'ByOrgProject',
  ByOrgProjectAllSms = 'ByOrgProjectAllSms',
  ByOrgProjectAllWhatsApp = 'ByOrgProjectAllWhatsApp',
  ByOrgProjectGlobalSmsExport = 'ByOrgProjectGlobalSmsExport',
  ByOrgProjectJourney = 'ByOrgProjectJourney',
  ByOrgProjectProviderBillingParty = 'ByOrgProjectProviderBillingParty',
  ByOrgProjectWhatsAppTemplateCategoryRecipientCountry = 'ByOrgProjectWhatsAppTemplateCategoryRecipientCountry',
  ByOrgProviderBillingParty = 'ByOrgProviderBillingParty',
  ByOrgRecipientCountry = 'ByOrgRecipientCountry',
  ByOrgWhatsAppTemplateCategory = 'ByOrgWhatsAppTemplateCategory',
}

/** Groups of related usage metrics */
export enum UsageMetricGroups {
  Catalog = 'Catalog',
  Events = 'Events',
  Jvt = 'Jvt',
  Messaging = 'Messaging',
  Sms = 'Sms',
  Users = 'Users',
  WhatsApp = 'WhatsApp',
}

export enum BillingParty {
  FirstParty = 'FirstParty',
  ThirdParty = 'ThirdParty',
  Unknown = 'Unknown',
}

export type MetricProject = {
  id: number;
  createdAt: number;
  name: string;
  isSandbox: boolean;
  isArchived: boolean;
};

// Metric components, shared across multiple Metric types
type Metadata = {
  /**
   * Metrics may have specific metadata associated. At time of this writing,
   * highWatermarkDate is only example. highWatermarkDate is provided in string
   * format like: '2022-12-08'
   */
  highWatermarkDate?: string;
  /** Last date relative to the metric *ie. 2025-01-01 */
  lastAvailableDate?: string;
};
type MetricName = string;
type OrganizationId = number;
type ProjectId = number;
type ProviderName = string;
type RecipientCountryCallingCode = string;
type RecipientCountryName = string;
type SenderPhoneNumberType = string;
type TemplateCategory = string; // may be enum
type Unit = string;
type Value = number;

export type Metric = {
  /** These fields are always present. */
  metricName: MetricName;
  organizationId: OrganizationId;
  unit: Unit;
  value: Value;
  /**
   * project is present for project-level metrics. In other words, when project
   * is present, the value will represent the metric count only for that project.
   */
  project?: MetricProject;
  metadata?: Metadata;
  /**
   * billingParty and providerName enable the per-provider (ie. Twilio, Telnyx),
   * and per-party (Iterable SMS or not) breakdowns for SMS.
   */
  billingParty?: string; // ie. FirstParty
  providerName?: ProviderName; // ie. Twilio
  /**
   * recipientCountryName and recipientCountryCallingCode enable the per-country
   * breakdowns for international SMS.
   */
  recipientCountryName?: RecipientCountryName; // ie. 'United States';
  recipientCountryCallingCode?: RecipientCountryCallingCode; // ie. '1';
  senderPhoneNumberType?: SenderPhoneNumberType;
  /** journey metric, only applicable for JvtCount */
  journeyId?: number;
  templateCategory?: TemplateCategory;
};

// Org metrics
export type OrgMetric = {
  metadata?: Metadata;
  metricName: MetricName;
  organizationId?: number;
  unit?: string;
  value: Value;
};

export type OrgBillingPartyMetric = OrgMetric & {
  billingParty: BillingParty;
};

export type OrgBillingPartyRecipientCountryMetric = OrgMetric & {
  billingParty: BillingParty;
  recipientCountryName: RecipientCountryName;
};

export type OrgProviderBillingPartyMetric = OrgMetric & {
  billingParty: BillingParty;
  providerName: ProviderName;
};

export type OrgWhatsAppTemplateCategoryMetric = OrgMetric & {
  templateCategory: TemplateCategory;
};

// Project metrics
export type OrgProjectMetric = {
  metricName: MetricName;
  organizationId?: number;
  projectId: ProjectId;
  unit?: string;
  value: Value;
};

export type OrgProjectGlobalSmsExportMetric = OrgProjectMetric & {
  billingParty: BillingParty;
  metadata: Metadata;
  recipientCountryCallingCode: RecipientCountryCallingCode;
  recipientCountryName: RecipientCountryName;
  senderPhoneNumberType: SenderPhoneNumberType;
};

export type OrgProjectJourneyMetric = OrgProjectMetric & {
  journeyId: number;
  metadata: Metadata;
  // metricName: 'JvtCount'; // only metric name for journey metrics
};

export type OrgProjectProviderBillingPartyMetric = OrgProjectMetric & {
  billingParty: BillingParty;
  metadata: Metadata;
  providerName: ProviderName;
};

export type OrgProjectWhatsAppTemplateCategoryRecipientCountryMetric = OrgProjectMetric & {
  metadata: Metadata;
  recipientCountryName: RecipientCountryName;
  templateCategory: TemplateCategory;
};
